import React, { useCallback, useEffect, useMemo, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../AuthProvider";
import {
  fetchAdminDriveGeometry,
  fetchAdminDrives,
  reviewAdminDrive,
} from "../services/driveReviewService";

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const statusLabels = {
  pending: "Ausstehend",
  approved: "Freigegeben",
  denied: "Abgelehnt",
};

const statusOptions = [
  { value: "all", label: "Alle Status" },
  { value: "pending", label: "Ausstehend" },
  { value: "approved", label: "Freigegeben" },
  { value: "denied", label: "Abgelehnt" },
];

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const StatusBadge = ({ status }) => (
  <span className={`car-pill car-pill-${status || "pending"}`}>
    {statusLabels[status] ?? status ?? "Unbekannt"}
  </span>
);

function PhotoPreview({ endpoint, fetchWithAuth, label }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!endpoint) {
      setPhotoUrl(null);
      setFailed(false);
      setLoading(false);
      return;
    }

    let active = true;
    let objectUrl = null;
    setLoading(true);
    setFailed(false);

    fetchWithAuth(endpoint)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Foto konnte nicht geladen werden.");
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setPhotoUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint, fetchWithAuth]);

  if (!endpoint) {
    return <div className="car-photo-placeholder">Kein Foto</div>;
  }

  if (loading) {
    return <div className="car-photo-placeholder">Lädt…</div>;
  }

  if (failed || !photoUrl) {
    return <div className="car-photo-placeholder">Fehler</div>;
  }

  return (
    <button
      type="button"
      className="drive-photo-button"
      onClick={() => window.open(photoUrl, "_blank", "noopener,noreferrer")}
    >
      <img src={photoUrl} alt={label} />
      <span>{label}</span>
    </button>
  );
}

function DriveMap({ geometry }) {
  const mapId = useMemo(
    () => `drive-map-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  useEffect(() => {
    if (!geometry || geometry.type !== "LineString" || !Array.isArray(geometry.coordinates)) {
      return undefined;
    }

    const container = document.getElementById(mapId);
    if (!container) {
      return undefined;
    }

    const points = geometry.coordinates
      .filter((coord) => Array.isArray(coord) && coord.length >= 2)
      .map((coord) => [coord[1], coord[0]]);

    if (!points.length) {
      return undefined;
    }

    const map = L.map(container, {
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    const polyline = L.polyline(points, {
      color: "#0f8a6d",
      weight: 4,
    }).addTo(map);

    L.circleMarker(points[0], {
      radius: 5,
      color: "#086a53",
      fillColor: "#086a53",
      fillOpacity: 1,
    }).addTo(map);

    L.circleMarker(points[points.length - 1], {
      radius: 6,
      color: "#b83232",
      fillColor: "#b83232",
      fillOpacity: 1,
    }).addTo(map);

    map.fitBounds(polyline.getBounds().pad(0.1));

    return () => {
      map.remove();
    };
  }, [geometry, mapId]);

  if (!geometry) {
    return <div className="car-empty">Keine Routendaten verfügbar.</div>;
  }

  return <div id={mapId} className="drive-map-canvas" />;
}

export default function FahrtenPage() {
  const { fetchWithAuth } = useAuth();
  const [drives, setDrives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [summaryDraft, setSummaryDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [geometryCache, setGeometryCache] = useState({});
  const [geometryLoading, setGeometryLoading] = useState({});
  const [geometryErrors, setGeometryErrors] = useState({});
  const [geometryRequested, setGeometryRequested] = useState({});

  const loadDrives = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAdminDrives(fetchWithAuth);
      setDrives(rows);
    } catch (err) {
      setError(err.message || "Fahrten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadDrives();
  }, [loadDrives]);

  useEffect(() => {
    if (!drives.length) {
      if (selectedDriveId !== null) {
        setSelectedDriveId(null);
      }
      return;
    }

    const selectionStillExists = drives.some((drive) => drive.id === selectedDriveId);
    if (!selectionStillExists) {
      setSelectedDriveId(drives[0].id);
    }
  }, [drives, selectedDriveId]);

  const filteredDrives = useMemo(() => {
    return drives.filter((drive) => {
      const haystack = [
        drive.driver_name,
        drive.driver_city,
        drive.number_plate,
        drive.manufacturer,
        drive.model,
        drive.id,
        drive.review_summary,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || (drive.review_status || "pending") === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [drives, searchTerm, statusFilter]);

  const selectedDrive = useMemo(
    () => drives.find((drive) => drive.id === selectedDriveId) || null,
    [drives, selectedDriveId]
  );

  useEffect(() => {
    setSummaryDraft(selectedDrive?.review_summary || "");
  }, [selectedDrive]);

  useEffect(() => {
    if (!selectedDriveId) {
      return;
    }

    if (Object.prototype.hasOwnProperty.call(geometryCache, selectedDriveId)) {
      return;
    }

    if (geometryRequested[selectedDriveId]) {
      return;
    }

    setGeometryRequested((current) => ({ ...current, [selectedDriveId]: true }));
    setGeometryLoading((current) => ({ ...current, [selectedDriveId]: true }));
    setGeometryErrors((current) => ({ ...current, [selectedDriveId]: null }));

    fetchAdminDriveGeometry(fetchWithAuth, selectedDriveId)
      .then((geometry) => {
        setGeometryCache((current) => ({ ...current, [selectedDriveId]: geometry }));
      })
      .catch((err) => {
        setGeometryErrors((current) => ({
          ...current,
          [selectedDriveId]: err.message || "Route konnte nicht geladen werden.",
        }));
      })
      .finally(() => {
        setGeometryLoading((current) => ({ ...current, [selectedDriveId]: false }));
      });
  }, [fetchWithAuth, geometryCache, geometryRequested, selectedDriveId]);

  useEffect(() => {
    if (!selectedDriveId) {
      return;
    }

    if (geometryLoading[selectedDriveId] && hasOwn(geometryCache, selectedDriveId)) {
      setGeometryLoading((current) => ({ ...current, [selectedDriveId]: false }));
    }
  }, [geometryCache, geometryLoading, selectedDriveId]);

  const submitReview = async (status) => {
    if (!selectedDrive) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await reviewAdminDrive(fetchWithAuth, selectedDrive.id, {
        status,
        summary: summaryDraft,
      });
      if (updated) {
        setDrives((current) =>
          current.map((drive) =>
            drive.id === selectedDrive.id ? { ...drive, ...updated } : drive
          )
        );
      }
    } catch (err) {
      setError(err.message || "Fahrt konnte nicht geprüft werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="portal-section invoice-panel">
      <header className="car-top">
        <div>
          <p className="car-eyebrow">Drive Review</p>
          <h1 className="portal-title">Fahrten</h1>
          <p className="portal-desc">
            Prüfe Fahrten anhand von Kontrollfotos, Verlauf und Auffälligkeiten und gib sie frei oder lehne sie ab.
          </p>
        </div>
        <div className="car-top-actions">
          <input
            className="car-search"
            placeholder="Suche nach Fahrer, Nummernschild oder Fahrt-ID"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select
            className="car-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="car-refresh" onClick={loadDrives} disabled={loading}>
            Neu laden
          </button>
        </div>
      </header>

      {error ? <div className="car-alert error">{error}</div> : null}

      <div className="drive-review-layout">
        <div className="invoice-list">
          <div className="invoice-table-top drive-table-top">
            <span>Fahrt</span>
            <span>Fahrer</span>
            <span>Auto</span>
            <span>Betrag</span>
            <span>Status</span>
          </div>
          {loading ? (
            <div className="car-empty">Fahrten werden geladen…</div>
          ) : filteredDrives.length === 0 ? (
            <div className="car-empty">Keine Fahrten gefunden.</div>
          ) : (
            filteredDrives.map((drive) => (
              <button
                key={drive.id}
                type="button"
                className={`invoice-row drive-row-card${selectedDriveId === drive.id ? " selected" : ""}`}
                onClick={() => setSelectedDriveId(drive.id)}
              >
                <div>
                  <div className="car-strong">{drive.id}</div>
                  <div className="car-muted">
                    {formatDate(drive.start_date)} bis {formatDate(drive.end_date)}
                  </div>
                </div>
                <div>
                  <div className="car-strong">{drive.driver_name || "Unbekannt"}</div>
                  <div className="car-muted">{drive.driver_city || "—"}</div>
                </div>
                <div>
                  <div className="car-strong">{drive.number_plate || "—"}</div>
                  <div className="car-muted">{drive.manufacturer} {drive.model}</div>
                </div>
                <div className="car-strong">{formatAmount(drive.cvalue)}</div>
                <StatusBadge status={drive.review_status || "pending"} />
              </button>
            ))
          )}
        </div>

        <div className="invoice-detail drive-review-detail">
          {!selectedDrive ? (
            <div className="car-empty">Wähle eine Fahrt aus, um Details und Fotos zu prüfen.</div>
          ) : (
            <>
              <div className="invoice-detail-header">
                <div>
                  <h2>{selectedDrive.id}</h2>
                  <p>
                    {selectedDrive.driver_name || "Unbekannt"} · {selectedDrive.number_plate || "—"}
                  </p>
                </div>
                <StatusBadge status={selectedDrive.review_status || "pending"} />
              </div>

              <div className="drive-review-meta">
                <div className="settings-row">
                  <span>Zeitraum</span>
                  <strong>
                    {formatDate(selectedDrive.start_date)} bis {formatDate(selectedDrive.end_date)}
                  </strong>
                </div>
                <div className="settings-row">
                  <span>Ertrag</span>
                  <strong>{formatAmount(selectedDrive.cvalue)}</strong>
                </div>
                <div className="settings-row">
                  <span>Zuletzt geprüft</span>
                  <strong>{formatDate(selectedDrive.reviewed_at)}</strong>
                </div>
              </div>

              <div className="drive-photo-grid">
                <div>
                  <div className="drive-photo-title">Vor der Fahrt</div>
                  <PhotoPreview
                    endpoint={selectedDrive.beforePhotoEndpoint}
                    fetchWithAuth={fetchWithAuth}
                    label="Foto öffnen"
                  />
                </div>
                <div>
                  <div className="drive-photo-title">Nach der Fahrt</div>
                  <PhotoPreview
                    endpoint={selectedDrive.afterPhotoEndpoint}
                    fetchWithAuth={fetchWithAuth}
                    label="Foto öffnen"
                  />
                </div>
              </div>

              <div>
                <div className="drive-photo-title">Gefahrene Route</div>
                {geometryErrors[selectedDrive.id] ? (
                  <div className="car-alert error">{geometryErrors[selectedDrive.id]}</div>
                ) : geometryLoading[selectedDrive.id] && !hasOwn(geometryCache, selectedDrive.id) ? (
                  <div className="car-empty">Route wird geladen…</div>
                ) : !hasOwn(geometryRequested, selectedDrive.id) ? (
                  <div className="car-empty">Route wird vorbereitet…</div>
                ) : (
                  <DriveMap geometry={geometryCache[selectedDrive.id] || null} />
                )}
              </div>

              <label className="settings-field">
                <span>Prüfnotiz</span>
                <textarea
                  rows={6}
                  className="settings-input"
                  value={summaryDraft}
                  onChange={(event) => setSummaryDraft(event.target.value)}
                  placeholder="Begründung für Freigabe oder Ablehnung"
                />
              </label>

              <div className="drive-review-actions">
                <button
                  type="button"
                  className="settings-reset"
                  onClick={() => submitReview("pending")}
                  disabled={saving}
                >
                  Auf ausstehend setzen
                </button>
                <button
                  type="button"
                  className="settings-save"
                  onClick={() => submitReview("approved")}
                  disabled={saving}
                >
                  Freigeben
                </button>
                <button
                  type="button"
                  className="drive-deny"
                  onClick={() => submitReview("denied")}
                  disabled={saving}
                >
                  Ablehnen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
